import { Button } from 'designSystem/components/Button'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import {
  printNormalizedPdf,
  printPdfNatively,
  printRasterized,
} from 'features/knowledgeDocumentViewer/utils/printDocument'
import { Loader2, Printer } from 'lucide-react'
import { useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'

function isPdfDocument(documentType: string | undefined): boolean {
  return documentType?.toLowerCase() === 'pdf'
}

function isChromeBrowser(): boolean {
  const ua = navigator.userAgent
  return ua.includes('Chrome') && !ua.includes('Edg') && !ua.includes('OPR')
}

function isSafariBrowser(): boolean {
  const ua = navigator.userAgent
  return (
    ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium')
  )
}

export function PrintDocumentButton() {
  const { documentSignedURL, documentType } = useDocumentTabDataContext()
  const isPrintingRef = useRef(false)
  const [isPreparing, setIsPreparing] = useState(false)

  const signedUrl = documentSignedURL?.signed_url

  const handlePrint = async () => {
    if (!signedUrl || isPrintingRef.current) return

    isPrintingRef.current = true
    setIsPreparing(true)

    const preOpenedWindow = isSafariBrowser()
      ? window.open('about:blank', '_blank')
      : null

    if (preOpenedWindow) {
      preOpenedWindow.document.write(
        '<html><body style="display:flex;align-items:center;justify-content:center;' +
          'height:100vh;margin:0;font-family:system-ui,sans-serif;color:#666;">' +
          'Preparing document for printing\u2026</body></html>'
      )
      preOpenedWindow.document.close()
    }

    try {
      if (!isPdfDocument(documentType)) {
        await printRasterized(signedUrl, preOpenedWindow)
      } else if (isChromeBrowser()) {
        try {
          await printNormalizedPdf(signedUrl)
        } catch {
          await printRasterized(signedUrl)
        }
      } else {
        try {
          await printPdfNatively(signedUrl, preOpenedWindow)
        } catch {
          await printRasterized(signedUrl, preOpenedWindow)
        }
      }
    } catch (error) {
      if (preOpenedWindow && !preOpenedWindow.closed) {
        preOpenedWindow.close()
      }
      console.error('Failed to print document:', error)
    } finally {
      isPrintingRef.current = false
      setIsPreparing(false)
    }
  }

  const icon = isPreparing ? (
    <SpinningLoader size={18} strokeWidth={1.5} />
  ) : (
    <Printer size={18} strokeWidth={1.5} />
  )

  return (
    <Button
      variant='inputRaised'
      size='icon-lg'
      title={isPreparing ? 'Preparing print...' : 'Print Document'}
      onClick={handlePrint}
      disabled={!signedUrl || isPreparing}>
      {icon}
    </Button>
  )
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const SpinningLoader = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`
